import { Component, ChangeDetectorRef, OnInit, OnDestroy, inject } from '@angular/core';
import { FormGroup, FormArray, FormBuilder, AsyncValidatorFn, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription as RxjsSubscription, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, tap, filter } from 'rxjs/operators';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent, CdkStep } from '@angular/cdk/stepper';
import { Store, ofActionSuccessful, Actions } from '@ngxs/store';
import { MbbProduct } from '../../models/mbb-product.model';
import { Subscription } from '@shared/models/subscription-model';
import {
  MbbProductAddAction,
  MbbProductUpdateAction,
  UpdateMbbDeliveryOptionAction,
  SetSelectedMbbStepIndexAction,
  ToggleSelectedMbbSubscriptionAction,
  UpdateMbbRouterDetailsAction,
} from '../../states/customer-product-mbb.actions';
import {
  ApiGetProductOfferingsMbbAction,
  ApiGetAllMbbRoutersAction,
} from '../../../../states/offering-mbb/offering-mbb.actions';
import { MbbRouter, MbbSubscriptionOffering, MbbAllowedChangeInfo } from '../../models/mbb-offerings-response.model';
import { MbbUserDetailsComponent } from './mbb-user-details/mbb-user-details.component';
import { MbbSubscriptionComponent } from './mbb-subscription/mbb-subscription.component';
import { MbbRouterComponent } from './mbb-select-router/mbb-router/mbb-router.component';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { ProductClassifications } from '@core/enums/product-classifications';
import { OpenDialogService } from '@core/services/open-dialog.service';
import { MessageType } from '@shared/enums/message-type.enum';
import { IconNames } from '@shared/enums/icon-names';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { MbbDeliveryOptions } from '../../models/mbb-delivery-options.model';
import { MbbSelectRouterComponent } from './mbb-select-router/mbb-select-router.component';
import { SimService } from '@sales/services/sim.service';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { SimCardType } from '@sales/models/simcard.model';
import { FormStatus } from '@sales/models/form-status.enum';
import { WarehouseStateSelectors } from '@sales/states/warehouse/warehouse.selectors';
import { WarehouseStateModel } from '@sales/states/warehouse/warehouse.state';
import { OfferingMbbSelectors } from '@sales/states/offering-mbb/offering-mbb.selectors';
import { CustomerProductMbbSelectors } from '../../states/customer-product-mbb.selectors';
import { ApiGetStockAction } from '@sales/states/warehouse/warehouse.actions';
import { CustomerSelectors } from '@sales/modules/details/states/customer.selectors';
import { InFlightOrder } from '@core/models/in-flight-order.model';
import { MbbRouterDetailsComponent } from './mbb-select-router/mbb-router-details/mbb-router-details.component';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { FormStateModel, FormWithStepper } from '@app/sales/states/form/form.types';
import { AppData } from '@app/shared/models/appdata.model';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiGetExistingSubscriptionsSuccessAction } from '@app/sales/modules/details/states/customer.actions';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { AsyncPipe, DatePipe } from '@angular/common';
import { MbbExistingSubscriptionComponent } from './mbb-existing-subscription/mbb-existing-subscription.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { IceBadgeComponent } from '@app/shared/components/atoms/ice-badge/ice-badge.component';
import { CustomerAgreementComponent } from '@app/shared/components/customer-agreement/customer-agreement.component';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceStepperComponent } from '@app/shared/components/molecules/ice-stepper/ice-stepper.component';
import { AccordionLazyDirective } from '@app/shared/directives/accordion-lazy.directive';
import { AccordionContentComponent } from '@app/shared/components/molecules/accordion/accordion-content/accordion-content.component';
import { MatMenuItem } from '@angular/material/menu';
import { AccordionHeaderComponent } from '@app/shared/components/molecules/accordion/accordion-header/accordion-header.component';
import { AccordionGroupComponent } from '@app/shared/components/molecules/accordion/accordion-group/accordion-group.component';
import { AccordionComponent } from '@app/shared/components/molecules/accordion/accordion.component';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';
import { AddProductButtonComponent } from '@app/shared/components/atoms/add-product-button/add-product-button.component';
import { MbbProductHelperService } from '@app/sales/services/mbb-product-helper.service';
import { getCorrelatedBundle } from '@app/core/utils';
import { MbbActivationDate } from './mbb-select-router/mbb-activation-date/mbb-activation-date.component';
import { FeatureGateService } from '@app/shared/services/featuregate.service';

@Component({
  selector: 'app-mbb',
  templateUrl: './mbb.component.html',
  styleUrls: ['./mbb.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
  ],
  imports: [
    AddProductButtonComponent,
    IceCardComponent,
    FormsModule,
    NgxsFormDirective,
    ReactiveFormsModule,
    AccordionComponent,
    AccordionGroupComponent,
    AccordionHeaderComponent,
    MatMenuItem,
    AccordionContentComponent,
    AccordionLazyDirective,
    IceStepperComponent,
    CdkStep,
    SpinnerComponent,
    MbbSubscriptionComponent,
    IceButtonComponent,
    MbbSelectRouterComponent,
    MbbUserDetailsComponent,
    CustomerAgreementComponent,
    IceBadgeComponent,
    IceAlertComponent,
    MbbExistingSubscriptionComponent,
    AsyncPipe,
    DatePipe,
    PriceFormatPipe,
    MbbActivationDate,
  ],
})
export class MbbComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private cd = inject(ChangeDetectorRef);
  private simService = inject(SimService);
  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private appData = inject(AppData);
  private openDialogService = inject(OpenDialogService);
  private actions$ = inject(Actions);
  private featureGateService = inject(FeatureGateService);

  PriceType = PriceType;
  MessageType = MessageType;
  IconNames = IconNames;
  MbbDeliveryOptions = MbbDeliveryOptions;
  FormStatus = FormStatus;
  SubscriptionHelperService = SubscriptionHelperService;

  formState$: Observable<FormStateModel> = inject(Store).select(state => state.formState);
  canSellHardwareWithLogisticsClaim$: Observable<boolean> = inject(Store).select(
    DealerStateSelectors.canSellHardwareWithLogistics
  );
  canSellMBBSubscriptionOnlyClaim$: Observable<boolean> = inject(Store).select(
    DealerStateSelectors.canSellMBBSubscriptionOnly
  );
  allowManualRegistrationClaim$: Observable<boolean> = inject(Store).select(DealerStateSelectors.canUseManualRegistration);
  mbbRouters$: Observable<MbbRouter[]> = inject(Store).select(OfferingMbbSelectors.routers);
  isLoadingMbbProducts$: Observable<boolean> = inject(Store).select(OfferingMbbSelectors.isLoadingMbbProducts);
  isLoadingMbbRouters$: Observable<boolean> = inject(Store).select(OfferingMbbSelectors.isLoadingMbbRouters);
  selectedMbbStepIndex$: Observable<number> = inject(Store).select(CustomerProductMbbSelectors.selectedStep);
  selectedMbbSubscriptionId$: Observable<string> = inject(Store).select(CustomerProductMbbSelectors.selectedSubscriptionId);
  mbbProducts$: Observable<MbbProduct[]> = inject(Store).select(CustomerProductMbbSelectors.products);
  warehouseState$: Observable<WarehouseStateModel> = inject(Store).select(WarehouseStateSelectors.getState);
  inFlightMbbOrders$: Observable<InFlightOrder[]> = inject(Store).select(CustomerSelectors.inFlightMbbOrders);
  hasPerformedAllowanceCheck$: Observable<boolean> = inject(Store).select(CustomerSelectors.hasPerformedAllowanceCheck);
  isMbbAllowanceCheckSuccessful$: Observable<boolean> = inject(Store).select(
    CustomerSelectors.isMbbAllowanceCheckSuccessful
  );
  mbbProductOfferings$: Observable<MbbSubscriptionOffering[]> = inject(Store).select(
    OfferingMbbSelectors.mbbProductOfferings
  );
  isLoadingProducts$: Observable<boolean> = inject(Store).select(CustomerSelectors.isLoadingProducts);

  allowSubscriptionOnlyWithBinding = this.featureGateService.isFeatureEnabled('featureMbbSubscriptionOnlyWithBinding');

  mbbAvailableRouters: MbbRouter[] = [];
  mbbAllowedOfferings: MbbAllowedChangeInfo[] = [];
  mbbProducts: MbbProduct[] = [];
  existingMbbProducts: MbbProduct[] = [];
  selectedStep: number;

  formState: FormStateModel;
  form: FormGroup = new FormGroup({ list: new FormArray([]) });

  subscriptions$: RxjsSubscription[] = [];
  stepperSelected = {};

  mbbSubscriptionHasError: boolean;
  mbbDeliveryHasError: boolean;
  mbbUserDetailsHasError: boolean;
  activationDateHasError: boolean = false;

  get list(): FormGroup[] {
    return (this.form.get('list') as FormArray).controls as FormGroup[];
  }

  public static buildFormFromExistingState(
    formState: FormStateModel,
    formBuilder: any,
    iccValidator: AsyncValidatorFn,
    store: Store
  ): FormGroup {
    const items = formState.mbbForm.model.list.map((item: FormWithStepper) => {
      const { productId, chooseMbbSubscription, chooseMbbRouter, mbbRouterDetails, mbbUserDetails, mbbDeliveryDetails } =
        item;

      const formGroup = formBuilder.group({
        productId: productId,
        interactedSteps: formBuilder.control(item.interactedSteps),
        activationDate: MbbActivationDate.buildActivationDateControl(new Date()),
        chooseMbbSubscription: MbbSubscriptionComponent.buildChooseSubscriptionFormGroup(chooseMbbSubscription.offering),
        chooseMbbRouter: MbbRouterComponent.buildChooseMbbRouterForm(chooseMbbRouter?.mbbRouter),
        mbbRouterDetails: MbbRouterDetailsComponent.buildMbbRouterDetailsForm(
          mbbRouterDetails?.id,
          mbbRouterDetails?.imei,
          mbbRouterDetails?.icc,
          iccValidator,
          store
        ),
        mbbUserDetails: MbbUserDetailsComponent.buildMbbUserDetailsForm(mbbUserDetails?.routerName),
        mbbDeliveryDetails: MbbSelectRouterComponent.buildMbbDeliveryDetailsForm(
          mbbDeliveryDetails.id,
          mbbDeliveryDetails.deliverySelection,
          mbbDeliveryDetails.orderSim,
          mbbDeliveryDetails.icc,
          iccValidator,
          store
        ),
      });

      MbbSelectRouterComponent.setFormValues(
        mbbDeliveryDetails.deliverySelection,
        formGroup.controls.mbbRouterDetails,
        formGroup.controls.chooseMbbRouter,
        formGroup.controls.mbbDeliveryDetails
      );

      switch (mbbDeliveryDetails.deliverySelection) {
        case MbbDeliveryOptions.SendRouterInPost:
        case MbbDeliveryOptions.SellInStore:
          formGroup.controls.mbbUserDetails.get('routerName').enable();
          break;
        case MbbDeliveryOptions.AlreadyOwnRouter:
          formGroup.controls.mbbUserDetails.get('routerName').patchValue(null);
          formGroup.controls.mbbUserDetails.get('routerName').disable();
          break;
      }

      return formGroup;
    });

    return formBuilder.group({
      list: formBuilder.array(items),
    });
  }

  ngOnInit() {
    // Load routers first to ensure bundles are available before filtering offerings
    this.store.dispatch(new ApiGetAllMbbRoutersAction());
    this.store.dispatch(new ApiGetProductOfferingsMbbAction());

    this.subscriptions$.push(
      this.selectedMbbSubscriptionId$.subscribe(id =>
        this.router.navigate([], { relativeTo: this.route, queryParams: { id } })
      )
    );
    this.openSelectedSubscription(this.route.snapshot.queryParamMap.get('id'));

    this.subscriptions$.push(
      this.formState$.subscribe(formState => {
        this.formState = formState;
      })
    );

    // Offerings with binding - must have available router, unless subscription only with binding is allowed
    // Offerings without binding - include all if dealer can sell mbbsubscriptiononly
    this.subscriptions$.push(
      combineLatest([
        this.mbbProducts$,
        this.mbbProductOfferings$,
        this.mbbRouters$.pipe(
          // Wait until routers are loaded with their bundles before processing
          filter((routers: MbbRouter[]) => {
            if (!routers) return false;
            // Ensure all routers have their bundles property initialized (not null/undefined)
            return routers.every((r: MbbRouter) => r.bundles !== null && r.bundles !== undefined);
          })
        ),
        this.allowManualRegistrationClaim$,
        this.canSellHardwareWithLogisticsClaim$,
        this.canSellMBBSubscriptionOnlyClaim$,
      ]).subscribe(
        ([
          mbbProducts,
          mbbProductOfferings,
          mbbRouters,
          allowManualRegistration,
          canSellHardwareWithLogistics,
          canSellMBBSubscriptionOnly,
        ]) => {
          const requireLogistics = !allowManualRegistration && canSellHardwareWithLogistics;
          this.mbbAvailableRouters = requireLogistics
            ? MbbProductHelperService.getAvailableLogisticsRouters(mbbRouters)
            : mbbRouters;

          const canSellAsSubscriptionOnlyFn = (offering: MbbSubscriptionOffering): boolean => {
            if (this.allowSubscriptionOnlyWithBinding) {
              return canSellMBBSubscriptionOnly;
            }
            return !offering.bindings || (offering.bindings.length === 0 && canSellMBBSubscriptionOnly);
          };
          const canSellAsBundleFn = (offering: MbbSubscriptionOffering): boolean => {
            if (!offering.bindings || offering.bindings.length === 0) {
              return false;
            }

            return this.mbbAvailableRouters.some(
              r => r.bundles && r.bundles.length > 0 && getCorrelatedBundle(r, offering.id)
            );
          };

          this.mbbAllowedOfferings = (mbbProductOfferings || [])
            .filter(x => canSellAsSubscriptionOnlyFn(x) || canSellAsBundleFn(x))
            .map(offering => ({ subscriptionOffering: offering }) as MbbAllowedChangeInfo);
          this.mbbProducts = mbbProducts.filter(p => !p.isExistingSubscription) || [];
          this.existingMbbProducts = mbbProducts.filter(p => p.isExistingSubscription) || [];

          this.updateFormFromMbbProducts();

          this.cd.markForCheck();
        }
      )
    );

    this.subscriptions$.push(this.selectedMbbStepIndex$.subscribe(val => (this.selectedStep = val)));

    this.subscriptions$.push(
      this.mbbRouters$.pipe(debounceTime(400), distinctUntilChanged()).subscribe(mbbRouters => {
        const offerings = mbbRouters.map(mbbRouter => mbbRouter.offeringId);
        this.store.dispatch(new ApiGetStockAction(offerings));
      })
    );

    this.form = MbbComponent.buildFormFromExistingState(
      this.formState,
      this.formBuilder,
      SimCardValidator(this.simService, SimCardType.Mbb),
      this.store
    );
  }

  onMbbSubscriptionHasError(isInvalid: boolean) {
    this.mbbSubscriptionHasError = isInvalid;
  }

  onMbbRouterHasError(isInvalid: boolean) {
    this.mbbDeliveryHasError = isInvalid;
  }

  onMbbUserDetailsHasError(isInvalid: boolean) {
    this.mbbUserDetailsHasError = isInvalid;
  }

  onStepChange({ previouslySelectedIndex, selectedIndex }: StepperSelectionEvent, i: number, formGroup: FormGroup) {
    const interactedSteps: number[] = formGroup.controls.interactedSteps.value;
    if (!interactedSteps.includes(previouslySelectedIndex)) {
      formGroup.controls.interactedSteps.setValue([...interactedSteps, previouslySelectedIndex]);
    }
    this.stepperSelected[i] = selectedIndex;
    this.store.dispatch(new SetSelectedMbbStepIndexAction(selectedIndex));
  }

  onToggleAccordion(_: boolean, id: string, item?: FormGroup) {
    if (item) {
      this.setInteractedStep(item);
    }
    this.store.dispatch(new ToggleSelectedMbbSubscriptionAction(id));
  }

  closeAccordion(id: string, item: FormGroup) {
    this.setInteractedStep(item);
    this.store.dispatch(new ToggleSelectedMbbSubscriptionAction(id));
  }

  onMbbSubscriptionChange(mbbProduct: MbbProduct, { offering, changeInfo }) {
    const update = { ...mbbProduct, mbbSubscription: offering, selectedChangeInfo: changeInfo };
    this.store.dispatch(new MbbProductUpdateAction(update));
  }

  onOrderSimChange(mbbProduct: MbbProduct, orderSim: boolean) {
    const update = { ...mbbProduct, orderSim };
    this.store.dispatch(new MbbProductUpdateAction(update));
  }

  onMbbRouterChange(mbbProduct: MbbProduct, mbbRouter: MbbRouter) {
    const update = { ...mbbProduct, mbbRouter };
    this.store.dispatch(new MbbProductUpdateAction(update));
  }

  onAddMbbProduct() {
    this.store.dispatch([new SetSelectedMbbStepIndexAction(0), new MbbProductAddAction(new MbbProduct())]);
  }

  onRemoveMbbProduct(product: MbbProduct) {
    this.openDialogService.openRemoveProductDialog(product, ProductClassifications.MBBProduct);
  }

  onMbbDeliveryChange(mbbProduct: MbbProduct, i: number, deliveryOption: MbbDeliveryOptions) {
    if (!mbbProduct) {
      return;
    }

    const mbbUserDetails = this.list[i].controls.mbbUserDetails;

    if (deliveryOption === MbbDeliveryOptions.SendRouterInPost) {
      if (mbbUserDetails) {
        mbbUserDetails.get('routerName').enable();
      }
    } else if (deliveryOption === MbbDeliveryOptions.SellInStore) {
      if (mbbUserDetails) {
        mbbUserDetails.get('routerName').enable();
      }
    } else if (deliveryOption === MbbDeliveryOptions.AlreadyOwnRouter) {
      if (mbbUserDetails) {
        mbbUserDetails.get('routerName').patchValue(null);
        mbbUserDetails.get('routerName').disable();
        this.onMbbUserDetailsHasError(false);
      }
    }

    this.store.dispatch(new UpdateMbbDeliveryOptionAction(mbbProduct.id, deliveryOption));
  }

  updateMbbRouterImei(imeiValue: string, mbbProduct: MbbProduct) {
    this.store.dispatch(new UpdateMbbRouterDetailsAction(mbbProduct.id, mbbProduct.icc, imeiValue, mbbProduct.routerName));
  }

  updateMbbRouterIcc(iccValue: string, mbbProduct: MbbProduct) {
    this.store.dispatch(new UpdateMbbRouterDetailsAction(mbbProduct.id, iccValue, mbbProduct.imei, mbbProduct.routerName));
  }

  updateMbbRouterName(routerName: string, mbbProduct: MbbProduct) {
    this.store.dispatch(new UpdateMbbRouterDetailsAction(mbbProduct.id, mbbProduct.icc, mbbProduct.imei, routerName));
  }

  updateMbbActivationDate(activationDate: Date | null, mbbProduct: MbbProduct) {
    const update: MbbProduct = { ...mbbProduct, activationDate };
    this.store.dispatch(new MbbProductUpdateAction(update));
  }

  updateFormFromMbbProducts() {
    const existingMbbProductsInForm = (this.form.get('list') as FormArray).controls;

    existingMbbProductsInForm.forEach(existing => {
      const existingId = existing.get('productId').value;

      if (!this.mbbProducts.find(p => p.id === existingId)) {
        const indexToRemove = existingMbbProductsInForm.indexOf(existing);
        (this.form.get('list') as FormArray).removeAt(indexToRemove);
      }
    });

    this.mbbProducts.forEach(product => {
      const existing = existingMbbProductsInForm.find(c => c.get('productId').value === product.id);
      if (!existing) {
        const mbbForm = this.createMbbSubscriptionItemFormGroup(product);
        (this.form.get('list') as FormArray).push(mbbForm);
      }
    });
  }

  openSelectedSubscription(subscriptionId: string) {
    return this.actions$
      .pipe(
        ofActionSuccessful(ApiGetExistingSubscriptionsSuccessAction),
        take(1), // We're only interested in the first emission
        tap(({ subscriptions }) => {
          const customerProduct: Subscription = subscriptions.find(
            (subscription: Subscription) => subscription.id.toString() === subscriptionId
          );
          if (customerProduct) {
            return this.store.dispatch(new ToggleSelectedMbbSubscriptionAction(customerProduct.id));
          }
        })
      )
      .subscribe();
  }

  toManny(product: MbbProduct) {
    const url = `${this.appData.mannyBaseUrl}/customer/${product.existingSubscription.customerId}/subscription/${product.existingSubscription.id}`;
    window.open(url, '_blank');
  }

  private createMbbSubscriptionItemFormGroup(item: MbbProduct) {
    const formGroup = this.formBuilder.group({
      productId: item.id,
      interactedSteps: this.formBuilder.control([]),
    });
    return formGroup;
  }

  private setInteractedStep(item: FormGroup) {
    const interactedSteps = item.controls.interactedSteps.value;
    if (!interactedSteps.includes(this.selectedStep)) {
      item.controls.interactedSteps.setValue([...interactedSteps, this.selectedStep]);
    }
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
